import { useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Search, Users as UsersIcon, Shield, ShieldOff, Ban, CheckCircle, Mail, Plus, Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useDebounce } from '@/hooks/use-debounce'
import { adminService, type AdminUser, type AdminOrganization } from '@/services/admin'
import type { AxiosError } from 'axios'

type StatusFilter = 'all' | 'active' | 'inactive' | 'verified' | 'unverified'
type SortOption = 'email_asc' | 'email_desc' | 'created_desc' | 'created_asc'

interface DialogState<T> {
  isOpen: boolean
  isProcessing: boolean
  error: string | null
  context: T | null
}

function createInitialDialogState<T>(): DialogState<T> {
  return {
    isOpen: false,
    isProcessing: false,
    error: null,
    context: null,
  }
}

function UserCardSkeleton() {
  return (
    <Card variant="glass" className="p-6 animate-pulse">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-gray-700/50" />
        <div className="flex-1 space-y-2">
          <div className="h-5 bg-gray-700/50 rounded w-1/2" />
          <div className="h-4 bg-gray-700/50 rounded w-3/4" />
          <div className="flex gap-2">
            <div className="h-5 w-16 bg-gray-700/50 rounded-full" />
            <div className="h-5 w-16 bg-gray-700/50 rounded-full" />
          </div>
        </div>
      </div>
    </Card>
  )
}

function EmptyState({ hasFilters, t }: { hasFilters: boolean; t: (key: string) => string }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-gray-700/30 flex items-center justify-center mb-6">
        <UsersIcon className="w-10 h-10 text-gray-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {hasFilters ? t('users.noResults') : t('users.noUsers')}
      </h3>
      <p className="text-gray-500 text-center max-w-md">
        {hasFilters ? t('users.noResultsDesc') : t('users.noUsersDesc')}
      </p>
    </div>
  )
}

interface UserCardProps {
  user: AdminUser
  onToggleActive: (user: AdminUser) => void
  onToggleSuperadmin: (user: AdminUser) => void
  onDelete: (user: AdminUser) => void
}

function UserCard({ user, onToggleActive, onToggleSuperadmin, onDelete }: UserCardProps) {
  const { t } = useTranslation('admin')

  return (
    <Card variant="glass" className="p-6 hover:bg-white/10 transition-colors">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-white">
            {user.full_name?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-100 truncate">
                {user.full_name || t('users.noName')}
              </h3>
              <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
                <Mail className="w-4 h-4" />
                <span className="truncate">{user.email}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            {user.is_active ? (
              <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">
                {t('users.active')}
              </span>
            ) : (
              <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
                {t('users.inactive')}
              </span>
            )}
            {user.is_verified && (
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
                {t('users.verified')}
              </span>
            )}
            {user.is_superadmin && (
              <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-medium rounded-full flex items-center gap-1">
                <Shield className="w-3 h-3" />
                {t('users.superadmin')}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
            <span>{t('users.organizations')}: {user.organizations_count}</span>
            <span>•</span>
            <span>{t('users.created')}: {new Date(user.created_at).toLocaleDateString()}</span>
            {user.last_login && (
              <>
                <span>•</span>
                <span>{t('users.lastLogin')}: {new Date(user.last_login).toLocaleDateString()}</span>
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              size="sm"
              variant={user.is_active ? 'outline' : 'success'}
              onClick={() => onToggleActive(user)}
              className="gap-2"
            >
              {user.is_active ? (
                <>
                  <Ban className="w-4 h-4" />
                  {t('users.deactivate')}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {t('users.activate')}
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant={user.is_superadmin ? 'outline' : 'action-purple'}
              onClick={() => onToggleSuperadmin(user)}
              className="gap-2"
            >
              {user.is_superadmin ? (
                <>
                  <ShieldOff className="w-4 h-4" />
                  {t('users.revokeSuperadmin')}
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  {t('users.grantSuperadmin')}
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => onDelete(user)}
            >
              {t('users.delete')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

const createUserSchema = z.object({
  email: z.string().email(),
  full_name: z.string().max(255).optional(),
  organization_id: z.string().uuid().optional().or(z.literal('')),
  organization_role: z.string().optional(),
})

type CreateUserInput = z.infer<typeof createUserSchema>

interface AdminCreateUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (user: AdminUser) => void
}

function AdminCreateUserDialog({ open, onOpenChange, onCreated }: AdminCreateUserDialogProps) {
  const { t } = useTranslation('admin')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')
  const [selectedRole, setSelectedRole] = useState<string>('')

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { email: '', full_name: '' },
  })

  useEffect(() => {
    if (!open) return
    const controller = new AbortController()
    adminService.listOrganizations(
      { status: 'active', page_size: 100 },
      { signal: controller.signal }
    ).then((res) => setOrganizations(res.organizations))
      .catch(() => {})
    return () => controller.abort()
  }, [open])

  const handleClose = (isOpen: boolean) => {
    if (!isSubmitting) {
      if (!isOpen) {
        reset()
        setSubmitError(null)
        setSelectedOrgId('')
        setSelectedRole('')
      }
      onOpenChange(isOpen)
    }
  }

  const onSubmit = async (data: CreateUserInput) => {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const response = await adminService.createUser({
        email: data.email,
        full_name: data.full_name || null,
        organization_id: selectedOrgId || null,
        organization_role: selectedOrgId && selectedRole ? selectedRole : null,
      })
      onCreated(response.user)
      reset()
      setSubmitError(null)
      setSelectedOrgId('')
      setSelectedRole('')
      onOpenChange(false)
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setSubmitError(axiosError.response?.data?.detail || t('users.create.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-gray-900 border-gray-700/50">
        <DialogHeader>
          <DialogTitle className="text-gray-100">{t('users.create.title')}</DialogTitle>
          <DialogDescription className="text-gray-400">
            {t('users.create.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="admin-user-email" className="text-gray-300">
              {t('users.create.emailLabel')} <span className="text-red-400">*</span>
            </Label>
            <Input
              id="admin-user-email"
              type="email"
              {...register('email')}
              placeholder={t('users.create.emailPlaceholder')}
              className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              disabled={isSubmitting}
            />
            {errors.email && <p className="text-xs text-red-400">{errors.email.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="admin-user-name" className="text-gray-300">
              {t('users.create.nameLabel')}
            </Label>
            <Input
              id="admin-user-name"
              {...register('full_name')}
              placeholder={t('users.create.namePlaceholder')}
              className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
              disabled={isSubmitting}
            />
            {errors.full_name && <p className="text-xs text-red-400">{errors.full_name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label className="text-gray-300">
              {t('users.create.orgLabel')}
            </Label>
            <Select
              value={selectedOrgId}
              onValueChange={(value) => {
                setSelectedOrgId(value === 'none' ? '' : value)
                if (value === 'none') setSelectedRole('')
              }}
            >
              <SelectTrigger className="bg-white/5 border-gray-700/50">
                <SelectValue placeholder={t('users.create.orgPlaceholder')} />
              </SelectTrigger>
              <SelectContent className="glass border-gray-700/50">
                <SelectItem value="none" className="focus:bg-white/5">
                  {t('users.create.orgPlaceholder')}
                </SelectItem>
                {organizations.map((org) => (
                  <SelectItem
                    key={org.id}
                    value={org.id}
                    className="focus:bg-white/5"
                  >
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedOrgId && (
            <div className="space-y-1.5">
              <Label className="text-gray-300">
                {t('users.create.orgRoleLabel')}
              </Label>
              <Select
                value={selectedRole || 'viewer'}
                onValueChange={setSelectedRole}
              >
                <SelectTrigger className="bg-white/5 border-gray-700/50">
                  <SelectValue placeholder={t('users.create.orgRolePlaceholder')} />
                </SelectTrigger>
                <SelectContent className="glass border-gray-700/50">
                  <SelectItem value="owner" className="focus:bg-white/5">Owner</SelectItem>
                  <SelectItem value="admin" className="focus:bg-white/5">Admin</SelectItem>
                  <SelectItem value="editor" className="focus:bg-white/5">Editor</SelectItem>
                  <SelectItem value="viewer" className="focus:bg-white/5">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <p className="text-xs text-gray-500 bg-white/5 rounded-md px-3 py-2">
            {t('users.create.hint')}
          </p>

          {submitError && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {submitError}
            </p>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={isSubmitting}
              className="border-gray-700/50"
            >
              {t('users.create.cancel')}
            </Button>
            <Button
              type="submit"
              variant="action-purple"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('users.create.submitting')}
                </>
              ) : (
                t('users.create.submit')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function AdminUsersPage() {
  const { t } = useTranslation('admin')
  const [users, setUsers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [sortOption, setSortOption] = useState<SortOption>('created_desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const [toggleActiveDialog, setToggleActiveDialog] = useState<DialogState<AdminUser>>(
    createInitialDialogState()
  )
  const [toggleSuperadminDialog, setToggleSuperadminDialog] = useState<DialogState<AdminUser>>(
    createInitialDialogState()
  )
  const [deleteDialog, setDeleteDialog] = useState<DialogState<AdminUser>>(
    createInitialDialogState()
  )
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const debouncedSearch = useDebounce(searchTerm, 300)

  const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: t('users.filters.all') },
    { value: 'active', label: t('users.filters.active') },
    { value: 'inactive', label: t('users.filters.inactive') },
    { value: 'verified', label: t('users.filters.verified') },
    { value: 'unverified', label: t('users.filters.unverified') },
  ]

  const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'email_asc', label: t('users.sort.emailAsc') },
    { value: 'email_desc', label: t('users.sort.emailDesc') },
    { value: 'created_desc', label: t('users.sort.createdDesc') },
    { value: 'created_asc', label: t('users.sort.createdAsc') },
  ]

  useEffect(() => {
    const controller = new AbortController()

    async function fetchUsers() {
      setIsLoading(true)
      try {
        const response = await adminService.listUsers(
          {
            search: debouncedSearch || undefined,
            status: statusFilter,
            page: currentPage,
            page_size: 12,
          },
          { signal: controller.signal }
        )
        setUsers(response.users)
        setTotalPages(response.total_pages)
      } catch (error) {
        if ((error as Error).name !== 'CanceledError') {
          console.error('Failed to fetch users:', error)
          setUsers([])
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchUsers()

    return () => {
      controller.abort()
    }
  }, [debouncedSearch, statusFilter, currentPage])

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      switch (sortOption) {
        case 'email_asc':
          return a.email.localeCompare(b.email)
        case 'email_desc':
          return b.email.localeCompare(a.email)
        case 'created_desc':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        case 'created_asc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        default:
          return 0
      }
    })
  }, [users, sortOption])

  const hasFilters = searchTerm !== '' || statusFilter !== 'all'

  const handleUserCreated = (user: AdminUser) => {
    setUsers((prev) => [user, ...prev])
  }

  const handleToggleActive = (user: AdminUser) => {
    setToggleActiveDialog({
      isOpen: true,
      isProcessing: false,
      error: null,
      context: user,
    })
  }

  const handleConfirmToggleActive = async () => {
    if (!toggleActiveDialog.context) return

    setToggleActiveDialog((prev) => ({ ...prev, isProcessing: true, error: null }))

    try {
      await adminService.updateUser(toggleActiveDialog.context.id, {
        is_active: !toggleActiveDialog.context.is_active,
      })

      setUsers((prev) =>
        prev.map((u) =>
          u.id === toggleActiveDialog.context?.id
            ? { ...u, is_active: !u.is_active }
            : u
        )
      )
      setToggleActiveDialog(createInitialDialogState())
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setToggleActiveDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || t('users.updateError'),
      }))
    }
  }

  const handleToggleSuperadmin = (user: AdminUser) => {
    setToggleSuperadminDialog({
      isOpen: true,
      isProcessing: false,
      error: null,
      context: user,
    })
  }

  const handleConfirmToggleSuperadmin = async () => {
    if (!toggleSuperadminDialog.context) return

    setToggleSuperadminDialog((prev) => ({ ...prev, isProcessing: true, error: null }))

    try {
      await adminService.updateUser(toggleSuperadminDialog.context.id, {
        is_superadmin: !toggleSuperadminDialog.context.is_superadmin,
      })

      setUsers((prev) =>
        prev.map((u) =>
          u.id === toggleSuperadminDialog.context?.id
            ? { ...u, is_superadmin: !u.is_superadmin }
            : u
        )
      )
      setToggleSuperadminDialog(createInitialDialogState())
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setToggleSuperadminDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || t('users.updateError'),
      }))
    }
  }

  const handleDelete = (user: AdminUser) => {
    setDeleteDialog({
      isOpen: true,
      isProcessing: false,
      error: null,
      context: user,
    })
  }

  const handleConfirmDelete = async () => {
    if (!deleteDialog.context) return

    setDeleteDialog((prev) => ({ ...prev, isProcessing: true, error: null }))

    try {
      await adminService.deleteUser(deleteDialog.context.id)
      setUsers((prev) => prev.filter((u) => u.id !== deleteDialog.context?.id))
      setDeleteDialog(createInitialDialogState())
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>
      setDeleteDialog((prev) => ({
        ...prev,
        isProcessing: false,
        error: axiosError.response?.data?.detail || t('users.deleteError'),
      }))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
            <UsersIcon className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">{t('users.title')}</h1>
            <p className="text-gray-400 text-sm">{t('users.subtitle')}</p>
          </div>
        </div>
        <Button
          variant="action-purple"
          className="gap-2"
          onClick={() => setCreateDialogOpen(true)}
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">{t('users.create.button')}</span>
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            type="text"
            placeholder={t('users.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger className="w-full sm:w-44 bg-white/5 border-gray-700/50">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="glass border-gray-700/50">
            {STATUS_OPTIONS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="focus:bg-white/5"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={sortOption}
          onValueChange={(value) => setSortOption(value as SortOption)}
        >
          <SelectTrigger className="w-full sm:w-44 bg-white/5 border-gray-700/50">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent className="glass border-gray-700/50">
            {SORT_OPTIONS.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="focus:bg-white/5"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {isLoading ? (
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              <UserCardSkeleton key={index} />
            ))}
          </>
        ) : sortedUsers.length === 0 ? (
          <EmptyState hasFilters={hasFilters} t={t} />
        ) : (
          sortedUsers.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              onToggleActive={handleToggleActive}
              onToggleSuperadmin={handleToggleSuperadmin}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            {t('users.previous')}
          </Button>
          <span className="flex items-center px-4 text-gray-400">
            {t('users.page')} {currentPage} {t('users.of')} {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            {t('users.next')}
          </Button>
        </div>
      )}

      <ConfirmationDialog
        open={toggleActiveDialog.isOpen}
        onOpenChange={(open) =>
          !open && !toggleActiveDialog.isProcessing && setToggleActiveDialog(createInitialDialogState())
        }
        title={
          toggleActiveDialog.context?.is_active
            ? t('users.deactivateTitle')
            : t('users.activateTitle')
        }
        description={
          toggleActiveDialog.context?.is_active
            ? t('users.deactivateDesc')
            : t('users.activateDesc')
        }
        confirmLabel={t('users.confirm')}
        confirmingLabel={t('users.confirming')}
        confirmVariant={toggleActiveDialog.context?.is_active ? 'action-red' : 'action-emerald'}
        isProcessing={toggleActiveDialog.isProcessing}
        error={toggleActiveDialog.error}
        onConfirm={handleConfirmToggleActive}
      />

      <ConfirmationDialog
        open={toggleSuperadminDialog.isOpen}
        onOpenChange={(open) =>
          !open &&
          !toggleSuperadminDialog.isProcessing &&
          setToggleSuperadminDialog(createInitialDialogState())
        }
        title={
          toggleSuperadminDialog.context?.is_superadmin
            ? t('users.revokeSuperadminTitle')
            : t('users.grantSuperadminTitle')
        }
        description={
          toggleSuperadminDialog.context?.is_superadmin
            ? t('users.revokeSuperadminDesc')
            : t('users.grantSuperadminDesc')
        }
        confirmLabel={t('users.confirm')}
        confirmingLabel={t('users.confirming')}
        confirmVariant="action-purple"
        isProcessing={toggleSuperadminDialog.isProcessing}
        error={toggleSuperadminDialog.error}
        onConfirm={handleConfirmToggleSuperadmin}
      />

      <ConfirmationDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) =>
          !open && !deleteDialog.isProcessing && setDeleteDialog(createInitialDialogState())
        }
        title={t('users.deleteTitle')}
        description={t('users.deleteDesc')}
        confirmLabel={t('users.deleteConfirm')}
        confirmingLabel={t('users.deleting')}
        confirmVariant="action-red"
        isProcessing={deleteDialog.isProcessing}
        error={deleteDialog.error}
        onConfirm={handleConfirmDelete}
      />

      <AdminCreateUserDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleUserCreated}
      />
    </div>
  )
}
